import { useState, useCallback, useId, ChangeEvent } from 'react';
import {
  Button,
  Field,
  FieldError,
  FieldDescription,
  FieldLabel,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { CheckIcon } from 'lucide-react';
import { useIsAiBusy } from '@/features/chats/stores';
import { ChatPopupNumberInputProps } from './ChatPopupNumberInput.types';
import { useTranslation } from 'react-i18next';

export const ChatPopupNumberInput = ({
  label,
  description,
  defaultValue,
  min,
  max,
  step = 1,
  isInteger = false,
  isMandatory,
  onChange,
}: ChatPopupNumberInputProps) => {
  const { t } = useTranslation('chats');

  const isAiBusy = useIsAiBusy();
  const isDisabled = isAiBusy;
  const [open, setOpen] = useState(false);
  // lazy initial value seeded from props.
  const [value, setValue] = useState<string>(() => defaultValue?.toString() ?? '');
  const [error, setError] = useState<string>('');
  const inputId = useId();

  const hasValue = value !== '';
  const showError = isMandatory && !hasValue;

  const validateAndSetValue = useCallback(
    (newValue: string) => {
      if (newValue === '') {
        setValue('');
        setError('');
        onChange?.(0);
        return;
      }

      const parsed = isInteger ? parseInt(newValue, 10) : parseFloat(newValue);

      if (isNaN(parsed)) {
        setError(t('chats.chatPopupNumberInput.pleaseEnterAValidNumber'));
        return;
      }

      if (min !== undefined && parsed < min) {
        setError(t('chats.chatPopupNumberInput.valueMustBeAtLeast', { min }));
        return;
      }

      if (max !== undefined && parsed > max) {
        setError(t('chats.chatPopupNumberInput.valueMustBeAtMost', { max }));
        return;
      }

      setError('');
      setValue(parsed.toString());
      onChange?.(parsed);
    },
    [isInteger, min, max, onChange, t],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      validateAndSetValue(newValue);
    },
    [validateAndSetValue],
  );

  const handleBlur = useCallback(() => {
    if (value && !error) {
      const parsed = isInteger ? parseInt(value, 10) : parseFloat(value);
      if (!isNaN(parsed)) {
        setValue(parsed.toString());
      }
    }
  }, [value, error, isInteger]);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setError('');
  }, []);

  const trigger = (
    <PopoverTrigger asChild>
      <Button
        type="button"
        disabled={isDisabled}
        variant="outline"
        aria-invalid={showError && open ? true : undefined}
        className="min-w-auto whitespace-nowrap"
      >
        {label} {hasValue && <CheckIcon className="size-4" />}
      </Button>
    </PopoverTrigger>
  );

  const rangeHint =
    min !== undefined && max !== undefined
      ? t('chats.chatPopupNumberInput.range', { min, max })
      : min !== undefined
        ? t('chats.chatPopupNumberInput.minimum', { min })
        : t('chats.chatPopupNumberInput.maximum', { max: max ?? '' });

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {description === undefined || description === '' ? (
        trigger
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent side="top">{description}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <PopoverContent align="start" side="top" className="w-70 p-4">
        <Field>
          <div className="mb-3 flex w-full items-center justify-between gap-x-2">
            <FieldLabel htmlFor={inputId} className="text-base font-normal text-muted-foreground">
              {label}
            </FieldLabel>
            <Button
              type="button"
              onClick={() => handleOpenChange(false)}
              variant="outline"
              size="sm"
            >
              {t('chats.chatPopupNumberInput.done')}
            </Button>
          </div>

          <Input
            id={inputId}
            type="number"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isDisabled}
            aria-invalid={error ? true : undefined}
            min={min}
            max={max}
            step={step}
            placeholder={
              isInteger
                ? t('chats.chatPopupNumberInput.enterInteger')
                : t('chats.chatPopupNumberInput.enterNumber')
            }
            className="bg-muted"
          />

          {error ? (
            <FieldError>{error}</FieldError>
          ) : (
            (min !== undefined || max !== undefined) && (
              <FieldDescription>{rangeHint}</FieldDescription>
            )
          )}
        </Field>
      </PopoverContent>
    </Popover>
  );
};
